import React, { useState, useEffect } from "react";
import { get, filter, find } from "lodash";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { PageRequestError } from "components/PageRequestError";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import { Dropdown } from "primereact/dropdown";
import useApp from "hooks/useApp";
import useApi from "hooks/useApi";
import useEditPage from "hooks/useEditPage";
import { useTranslation } from "react-i18next";
const AssignMetric = (props) => {
  const app = useApp();
  const api = useApi();
  const { t } = useTranslation();
  const SELECT_METRIC = t("SELECT_METRIC");
  const LOADING = t("LOADING");
  const [metrics, setMetrics] = useState([]);
  const [metricsFetched, setMetricsFetched] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedMetricsUpdated, setSelectedMetricsUpdated] = useState(false);
  // form validation schema
  const validationSchema = yup.object().shape({
    org_code: yup.string().required(t("FACILITY_REQUIRED")),
    module_name: yup.string().required(t("NAME_REQUIRED")),
    assigned_metric: yup.string().required(t("METRIC_REQUIRED")),
  });
  // form default values
  const formDefaultValues = {
    org_code: "",
    module_name: "",
    assigned_metric: "",
  };
  //where page logics resides
  const pageController = useEditPage({ props, formDefaultValues, afterSubmit });
  //destructure and grab what we need
  const {
    formData,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    apiRequestError,
    inputClassName,
  } = pageController;
  useEffect(() => {
    if (app.isDialogOpen()) {
      const bodyEleSty = get(document, "body.style");
      if (bodyEleSty) {
        bodyEleSty.overflow = "hidden";
      }
    } else {
      const bodyEleSty = get(document, "body.style");
      if (bodyEleSty) {
        bodyEleSty.overflow = "";
      }
    }
  }, [app.isDialogOpen()]);
  //Event raised on form submit success
  function afterSubmit(response) {
    const latestCounter = props.updateCounter + 1;
    props.setUpdateCounter(latestCounter);
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(`/modules/view/:pageid`);
    }
  }

  useEffect(() => {
    if (
      get(props, "selectedOrgCode") &&
      selectedMetrics &&
      selectedMetricsUpdated
    ) {
      setMetricsFetched(false);
      api
        .get(`metrics/get_metrics_by_org/${props.selectedOrgCode}`)
        .then((res) => {
          const metrics = get(res, "data.data", []);
          const filteredMetrics = filter(
            metrics,
            (metric) =>
              !find(selectedMetrics, (metricId) => metricId === metric.id)
          );
          setMetrics(filteredMetrics);
          setMetricsFetched(true);
          setSelectedMetricsUpdated(false);
        })
        .catch((e) => {
          setMetrics([]);
          setMetricsFetched(true);
          setSelectedMetricsUpdated(false);
        });
    }
  }, [props.selectedOrgCode, selectedMetrics]);

  // loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }
  //display error page
  if (apiRequestError) {
    return <PageRequestError error={apiRequestError} />;
  }
  const itemTemplate = (option) => {
    return (
      <div>
        {option.id} - {option.name}
      </div>
    );
  };

  //page is ready when formdata loaded successfully
  if (pageReady) {
    return (
      <main id="AssignMetric" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={t("ASSIGN_METRIC")}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-9 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                      submitForm(values);
                    }}
                  >
                    {(formik) => {
                      if (
                        get(formik, "values.metrics", []).length > 0 &&
                        selectedMetrics.length !==
                          get(formik, "values.metrics", []).length
                      ) {
                        setSelectedMetrics(get(formik, "values.metrics", []));
                        setSelectedMetricsUpdated(true);
                      }
                      return (
                        <Form className={`${!props.isSubPage ? "card  " : ""}`}>
                          <div className="grid">
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("FACILITIES")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    name="org_code"
                                    value={formik.values.org_code}
                                    onChange={formik.handleChange}
                                    options={get(props, "organisations", [])}
                                    optionLabel="name"
                                    optionValue="code"
                                    disabled
                                    placeholder={t("SELECT_FACILITY")}
                                    className={inputClassName(
                                      formik?.errors?.org_code
                                    )}
                                  />
                                  <ErrorMessage
                                    name="org_code"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("NAME")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="module_name"
                                    onChange={formik.handleChange}
                                    value={formik.values.module_name}
                                    label="Name"
                                    type="text"
                                    placeholder={t("ENTER_NAME")}
                                    className={inputClassName(
                                      formik?.errors?.module_name
                                    )}
                                    disabled
                                  />
                                  <ErrorMessage
                                    name="module_name"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("METRICS")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    name="assigned_metric"
                                    value={formik.values.assigned_metric}
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "assigned_metric",
                                        e.value
                                      );
                                    }}
                                    options={metrics}
                                    emptyMessage={t(
                                      "NO_METRIC_AVAILABLE_TO_ASSIGN"
                                    )}
                                    loading={!metricsFetched}
                                    optionLabel="name"
                                    optionValue="id"
                                    placeholder={
                                      metricsFetched ? SELECT_METRIC : LOADING
                                    }
                                    className={inputClassName(
                                      formik?.errors?.assigned_metric
                                    )}
                                    {...(formik.values.assigned_metric && {
                                      showClear: true,
                                    })}
                                    itemTemplate={itemTemplate}
                                  />
                                  <ErrorMessage
                                    name="assigned_metric"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {props.showFooter && (
                            <div className="text-center my-3">
                              <Button
                                onClick={(e) => handleSubmit(e, formik)}
                                type="submit"
                                label={t("ASSIGN")}
                                icon="pi pi-send"
                                loading={saving}
                              />
                            </div>
                          )}
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};
const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "assignMetric",
      getApiPath: "modules/get_module",
      putApiPath: "modules/edit_module",
      routeName: "metricsAssign",
      submitButtonLabel: "Assign",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("UPDATE_METRIC"),
      msgAfterSave: t("METRIC_UPDATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: true,
    };
    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(AssignMetric);
